import DOMPurify from 'isomorphic-dompurify'

function sanitize(html: string, allowedTags: string[], allowedAttrs: string[]): string {
    // Convert just text alignment to a constrained attribute. All arbitrary inline CSS is removed.
    const fragment = DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: [...allowedAttrs, 'style'],
        ALLOW_DATA_ATTR: false,
        RETURN_DOM_FRAGMENT: true
    })
    for (const element of fragment.querySelectorAll('[style]')) {
        const alignment = (element as HTMLElement).style.textAlign
        if (
            allowedAttrs.includes('data-text-align') &&
            /^(P|H[1-6])$/.test(element.tagName) &&
            /^(left|center|right)$/.test(alignment)
        ) {
            element.setAttribute('data-text-align', alignment)
        }
        element.removeAttribute('style')
    }
    for (const element of fragment.querySelectorAll('[data-text-align]')) {
        if (
            !/^(P|H[1-6])$/.test(element.tagName) ||
            !/^(left|center|right)$/.test(element.getAttribute('data-text-align') || '')
        ) {
            element.removeAttribute('data-text-align')
        }
    }
    for (const element of fragment.querySelectorAll('[data-callout-type]')) {
        if (!/^(info|warning|success|error)$/.test(element.getAttribute('data-callout-type') || '')) {
            element.setAttribute('data-callout-type', 'info')
        }
    }
    for (const link of fragment.querySelectorAll('a[target="_blank"]')) {
        link.setAttribute('rel', 'noopener noreferrer')
    }
    const container = fragment.ownerDocument.createElement('div')
    container.append(fragment)
    return container.innerHTML.replace(/<CookieIframe\b[^>]*>[\s\S]*?<\/CookieIframe>/gi, (iframe) => {
        // Only allow the editor's YouTube embeds, never arbitrary external documents.
        const source = iframe.match(/\bsrc="([^"]*)"/i)?.[1]
        return source &&
            /^https:\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com)\/embed\/[a-zA-Z0-9_-]+(?:[?][^"<>]*)?$/.test(
                source
            )
            ? iframe
            : ''
    })
}

/** The same HTML and URL policy is applied during SSR and hydration. */
export function sanitizeBlogHtml(html: string): string {
    return sanitize(
        html,
        [
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'p',
            'br',
            'strong',
            'em',
            'u',
            's',
            'mark',
            'a',
            'img',
            'ul',
            'ol',
            'li',
            'blockquote',
            'code',
            'pre',
            'figure',
            'figcaption',
            'table',
            'thead',
            'tbody',
            'tr',
            'th',
            'td',
            'hr',
            'div',
            'span',
            'iframe'
        ],
        [
            'href',
            'title',
            'target',
            'rel',
            'src',
            'alt',
            'width',
            'height',
            'start',
            'reversed',
            'colspan',
            'rowspan',
            'class',
            'id',
            'frameborder',
            'allow',
            'allowfullscreen',
            'data-youtube-video',
            'data-text-align',
            'data-lead',
            'data-callout',
            'data-callout-type'
        ]
    )
}

export function sanitizeBlogBio(html: string): string {
    return sanitize(html, ['p', 'br', 'strong', 'em'], [])
}
